// Featured Bundle block runtime: bulk add-to-cart for the rows the merchant configured.

import { CartAddEvent } from '@theme/events';

class BundleCardComponent extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  addAllButton = null;
  /** @type {((event: Event) => void) | undefined} */
  handleAddAll;

  connectedCallback() {
    this.addAllButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-bundle-card-add-all]')
    );
    if (!this.addAllButton) return;

    const handler = this.onAddAll.bind(this);
    this.handleAddAll = handler;
    this.addAllButton.addEventListener('click', handler);
  }

  disconnectedCallback() {
    if (this.addAllButton && this.handleAddAll) {
      this.addAllButton.removeEventListener('click', this.handleAddAll);
    }
  }

  /** @returns {number[]} */
  collectVariantIds() {
    const rows = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.bundle-product[data-variant-id][data-variant-available="true"]')
    );
    return Array.from(rows)
      .map((row) => parseInt(row.dataset.variantId ?? '', 10))
      .filter((id) => Number.isFinite(id) && id > 0);
  }

  /** @param {boolean} loading */
  setButtonLoading(loading) {
    if (!this.addAllButton) return;
    this.addAllButton.disabled = loading;
    this.addAllButton.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  flashError() {
    if (!this.addAllButton) return;
    this.addAllButton.setAttribute('data-error', 'true');
    setTimeout(() => this.addAllButton?.removeAttribute('data-error'), 2000);
  }

  /** @param {Event} event */
  async onAddAll(event) {
    event.preventDefault();
    if (!this.addAllButton || this.addAllButton.disabled) return;

    const variantIds = this.collectVariantIds();
    if (variantIds.length === 0) return;

    this.setButtonLoading(true);

    try {
      const cartItemSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
        .filter(/** @returns {id is string} */ (id) => Boolean(id));

      /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string }} */
      const payload = {
        items: variantIds.map((id) => ({ id, quantity: 1 })),
      };
      if (cartItemSectionIds.length) {
        payload.sections = cartItemSectionIds.join(',');
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Cart add failed: ${response.status}`);

      const result = /** @type {{ sections?: Record<string, string> }} */ (await response.json());
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'bundle-card', {
          source: 'bundle-card',
          itemCount: variantIds.length,
          sections: result.sections,
        })
      );
    } catch {
      this.flashError();
    } finally {
      this.setButtonLoading(false);
    }
  }
}

if (!customElements.get('bundle-card-component')) {
  customElements.define('bundle-card-component', BundleCardComponent);
}
